import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthProvider';
import { dataIsoLocal, destinoNotificacao } from '@/features/notificacoes/destinoNotificacao';
import { notificacaoKeys } from '@/features/notificacoes/useNotificacaoQueries';
import { useToast } from '@/shared/hooks/useToast';
import {
  registerPushToken,
  subscribeForegroundPush,
  type AvisoForeground,
} from '@/shared/lib/push/pushClient';
import { isPushConfigured } from '@/shared/lib/push/firebaseConfig';

type Options = {
  enabled?: boolean;
};

export type PushPermissionState = 'default' | 'granted' | 'denied' | 'unavailable';

function idPositivo(valor?: string): number | null {
  const n = Number(valor);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Mesma rota do clique no sino e no push em background. */
function destinoAvisoForeground(aviso: AvisoForeground): string | null {
  if (aviso.tipo === 'CHAT_MENSAGEM') {
    const id = idPositivo(aviso.idConversa);
    return id ? `/chat/${id}` : null;
  }
  if (aviso.tipo === 'EVENTO') {
    const idEvento = idPositivo(aviso.idEvento);
    if (idEvento == null) return null;
    return destinoNotificacao({
      idEvento,
      dataEvento: aviso.dataEvento,
      criadoEm: aviso.dataEvento ? `${aviso.dataEvento}T00:00:00` : null,
    });
  }
  if (aviso.tipo !== 'PUBLICACAO') return null;
  const idPublicacao = idPositivo(aviso.idPublicacao);
  if (idPublicacao == null) return null;
  const dia = aviso.dataPublicacao && /^\d{4}-\d{2}-\d{2}$/.test(aviso.dataPublicacao)
    ? aviso.dataPublicacao
    : dataIsoLocal();
  return destinoNotificacao({
    idPublicacao,
    idAluno: idPositivo(aviso.idAluno),
    idComentario: idPositivo(aviso.idComentario),
    criadoEm: `${dia}T00:00:00`,
  });
}

function lerPermissao(): PushPermissionState {
  if (typeof Notification === 'undefined') {
    return 'unavailable';
  }
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  if (Notification.permission === 'denied') {
    return 'denied';
  }
  return 'default';
}

/**
 * Registra token FCM apos sessao pronta e mostra toast em foreground.
 * Se a permissao ainda nao foi concedida (ou o registro falhou), expoe
 * `precisaAtivar` para um banner persistente. Chrome exige gesto do usuario
 * para `requestPermission`.
 */
export function usePushNotifications({ enabled = true }: Options = {}) {
  const { authGate, isAuthenticated } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const startedRef = useRef(false);
  const [precisaAtivar, setPrecisaAtivar] = useState(false);
  const [permissaoBloqueada, setPermissaoBloqueada] = useState(false);
  const [ativando, setAtivando] = useState(false);
  const [permTick, setPermTick] = useState(0);

  const marcarPendente = useCallback((bloqueada: boolean) => {
    setPrecisaAtivar(true);
    setPermissaoBloqueada(bloqueada);
  }, []);

  const marcarAtivo = useCallback(() => {
    setPrecisaAtivar(false);
    setPermissaoBloqueada(false);
  }, []);

  /**
   * Aviso em foreground: o navegador nao mostra banner do sistema, entao o toast
   * faz esse papel. Publicacao e comentario tambem atualizam o sino.
   */
  const mostrarAviso = useCallback(
    (aviso: AvisoForeground) => {
      const destino = destinoAvisoForeground(aviso);
      toast.info(aviso.corpo ? `${aviso.titulo}: ${aviso.corpo}` : aviso.titulo, {
        onClick: destino ? () => navigate(destino) : undefined,
      });
      if (aviso.tipo === 'PUBLICACAO' || aviso.tipo === 'EVENTO') {
        void queryClient.invalidateQueries({ queryKey: notificacaoKeys.all });
      }
    },
    [navigate, queryClient, toast],
  );

  useEffect(() => {
    if (!enabled || !isAuthenticated || authGate !== 'ready' || !isPushConfigured()) {
      return;
    }

    const bump = () => setPermTick((n) => n + 1);
    document.addEventListener('visibilitychange', bump);

    let permStatus: PermissionStatus | null = null;
    const podeConsultar = navigator.permissions?.query != null;
    if (podeConsultar) {
      void navigator.permissions
        .query({ name: 'notifications' as PermissionName })
        .then((status) => {
          permStatus = status;
          status.onchange = bump;
        })
        .catch(() => {
          /* Permissions API pode recusar o nome em alguns browsers */
        });
    }

    return () => {
      document.removeEventListener('visibilitychange', bump);
      if (permStatus) {
        permStatus.onchange = null;
      }
    };
  }, [enabled, isAuthenticated, authGate]);

  useEffect(() => {
    if (!enabled || !isAuthenticated || authGate !== 'ready' || !isPushConfigured()) {
      return;
    }

    let unsub: (() => void) | null = null;
    let cancelled = false;

    void (async () => {
      const estado = lerPermissao();
      if (estado === 'unavailable') {
        console.warn('Push: Notification API indisponivel');
        marcarPendente(false);
        return;
      }
      if (estado === 'denied') {
        console.warn('Push: permissao bloqueada no navegador');
        marcarPendente(true);
        return;
      }
      if (estado !== 'granted') {
        // Sem gesto do usuario o Chrome nao abre o prompt de forma confiavel.
        marcarPendente(false);
        return;
      }
      try {
        if (!startedRef.current) {
          startedRef.current = true;
          const token = await registerPushToken();
          if (cancelled) return;
          if (!token) {
            startedRef.current = false;
            console.warn('Push: getToken/registro retornou vazio');
            marcarPendente(lerPermissao() === 'denied');
            return;
          }
        }
        if (cancelled) return;
        marcarAtivo();
        unsub = await subscribeForegroundPush(mostrarAviso);
      } catch (err) {
        console.warn('Push FCM nao registrado', err);
        startedRef.current = false;
        marcarPendente(lerPermissao() === 'denied');
      }
    })();

    return () => {
      cancelled = true;
      unsub?.();
    };
  }, [
    enabled,
    isAuthenticated,
    authGate,
    toast,
    navigate,
    permTick,
    marcarPendente,
    marcarAtivo,
    mostrarAviso,
  ]);

  const ativar = async () => {
    if (!isPushConfigured() || ativando) return;
    setAtivando(true);
    try {
      const token = await registerPushToken();
      if (!token) {
        marcarPendente(lerPermissao() === 'denied');
        toast.warning(
          lerPermissao() === 'denied'
            ? 'O navegador bloqueou as notificacoes deste site. Desbloqueie no cadeado da barra de endereco. Em janela anonima o Chrome nao permite notificacoes — use uma aba normal.'
            : 'Nao foi possivel ativar notificacoes. Verifique a permissao do site e o modo Nao perturbe do Windows.',
        );
        return;
      }
      startedRef.current = true;
      marcarAtivo();
      toast.success(
        'Notificacoes ativadas. Se nada aparecer, desative o Nao perturbe no Windows.',
      );
      await subscribeForegroundPush(mostrarAviso);
    } catch (err) {
      console.warn('Push FCM nao registrado', err);
      startedRef.current = false;
      marcarPendente(lerPermissao() === 'denied');
      toast.warning('Falha ao ativar notificacoes. Verifique a permissao do site.');
    } finally {
      setAtivando(false);
    }
  };

  return { precisaAtivar, permissaoBloqueada, ativando, ativar };
}
